import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ApiResponse } from '../models/common.model';
import {
  SessionPlansResponse,
  SessionPlanResponse,
  CreateSessionPlanRequest,
  UpdateSessionPlanRequest,
  SessionPurchasesResponse,
  SessionPurchaseResponse,
  AttendanceRecordsResponse,
  RecordAttendanceRequest,
  AttendanceReportResponse,
  SportsResponse,
  Sport,
  SportImage,
  AdminCreatePurchaseRequest
} from '../models/session.model';
import { RecordPaymentRequest, RecordPaymentResponse, PaymentHistoryResponse } from '../models/payment.model';

@Injectable({
  providedIn: 'root'
})
export class SessionService {
  private apiUrl = `${environment.apiUrl}/session`;

  constructor(private http: HttpClient) {}

  // Sports (from appconfig)
  getSports(): Observable<SportsResponse> {
    return this.http.get<SportsResponse>(`${environment.apiUrl}/appconfig/sports-list`);
  }

  createSport(data: { name: string; description?: string }): Observable<ApiResponse<Sport>> {
    return this.http.post<ApiResponse<Sport>>(`${environment.apiUrl}/sport`, data);
  }

  uploadSportImage(sportId: string, file: File): Observable<ApiResponse<string>> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<ApiResponse<string>>(`${environment.apiUrl}/sport/${sportId}/upload-image`, formData);
  }

  updateSport(sportId: string, data: { name?: string; description?: string; isActive?: boolean; isComingSoon?: boolean }): Observable<ApiResponse<null>> {
    return this.http.put<ApiResponse<null>>(`${environment.apiUrl}/sport/${sportId}`, data);
  }

  reorderSports(sportIds: string[]): Observable<ApiResponse<null>> {
    return this.http.put<ApiResponse<null>>(`${environment.apiUrl}/sport/reorder`, { sportIds });
  }

  getSportDetail(sportId: string): Observable<ApiResponse<Sport>> {
    return this.http.get<ApiResponse<Sport>>(`${environment.apiUrl}/sport/${sportId}/detail`);
  }

  uploadGalleryImage(sportId: string, file: File): Observable<ApiResponse<SportImage>> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<ApiResponse<SportImage>>(`${environment.apiUrl}/sport/${sportId}/gallery`, formData);
  }

  deleteGalleryImage(sportId: string, imageId: string): Observable<ApiResponse<null>> {
    return this.http.delete<ApiResponse<null>>(`${environment.apiUrl}/sport/${sportId}/gallery/${imageId}`);
  }

  reorderGalleryImages(sportId: string, imageIds: string[]): Observable<ApiResponse<null>> {
    return this.http.put<ApiResponse<null>>(`${environment.apiUrl}/sport/${sportId}/gallery/reorder`, { imageIds });
  }

  // Plans
  getPlans(sportId?: string): Observable<SessionPlansResponse> {
    let params = new HttpParams();
    if (sportId) params = params.set('sportId', sportId);
    return this.http.get<SessionPlansResponse>(`${this.apiUrl}/plans`, { params });
  }

  createPlan(request: CreateSessionPlanRequest): Observable<SessionPlanResponse> {
    return this.http.post<SessionPlanResponse>(`${this.apiUrl}/plans`, request);
  }

  updatePlan(planId: string, request: UpdateSessionPlanRequest): Observable<SessionPlanResponse> {
    return this.http.put<SessionPlanResponse>(`${this.apiUrl}/plans/${planId}`, request);
  }

  deletePlan(planId: string): Observable<ApiResponse<null>> {
    return this.http.delete<ApiResponse<null>>(`${this.apiUrl}/plans/${planId}`);
  }

  // Purchases
  getPurchases(sportId?: string, year?: number, month?: number, status?: string, branchId?: string): Observable<SessionPurchasesResponse> {
    let params = new HttpParams();
    if (sportId) params = params.set('sportId', sportId);
    if (branchId) params = params.set('branchId', branchId);
    if (year) params = params.set('year', year.toString());
    if (month) params = params.set('month', month.toString());
    if (status) params = params.set('status', status);
    return this.http.get<SessionPurchasesResponse>(`${this.apiUrl}/purchases`, { params });
  }

  getPurchaseDetails(purchaseId: string): Observable<SessionPurchaseResponse> {
    return this.http.get<SessionPurchaseResponse>(`${this.apiUrl}/purchases/${purchaseId}`);
  }

  adminCreatePurchase(request: AdminCreatePurchaseRequest): Observable<SessionPurchaseResponse> {
    return this.http.post<SessionPurchaseResponse>(`${this.apiUrl}/admin-purchase`, request);
  }

  // Payments
  recordPayment(purchaseId: string, request: RecordPaymentRequest): Observable<ApiResponse<RecordPaymentResponse>> {
    return this.http.post<ApiResponse<RecordPaymentResponse>>(`${this.apiUrl}/purchases/${purchaseId}/payments`, request);
  }

  getPaymentHistory(purchaseId: string): Observable<ApiResponse<PaymentHistoryResponse>> {
    return this.http.get<ApiResponse<PaymentHistoryResponse>>(`${this.apiUrl}/purchases/${purchaseId}/payments`);
  }

  // Attendance
  recordAttendance(request: RecordAttendanceRequest): Observable<ApiResponse<null>> {
    return this.http.post<ApiResponse<null>>(`${this.apiUrl}/attendance`, request);
  }

  undoAttendance(attendanceId: string): Observable<ApiResponse<null>> {
    return this.http.delete<ApiResponse<null>>(`${this.apiUrl}/attendance/${attendanceId}`);
  }

  getAttendanceHistory(userId?: string, sportId?: string, year?: number, month?: number): Observable<AttendanceRecordsResponse> {
    let params = new HttpParams();
    if (userId) params = params.set('userId', userId);
    if (sportId) params = params.set('sportId', sportId);
    if (year) params = params.set('year', year.toString());
    if (month) params = params.set('month', month.toString());
    return this.http.get<AttendanceRecordsResponse>(`${this.apiUrl}/attendance/history`, { params });
  }

  // Reports
  getAttendanceReport(year: number, month: number, sportId?: string): Observable<AttendanceReportResponse> {
    let params = new HttpParams()
      .set('year', year.toString())
      .set('month', month.toString());
    if (sportId) params = params.set('sportId', sportId);
    return this.http.get<AttendanceReportResponse>(`${this.apiUrl}/reports/attendance`, { params });
  }
}
